import { SECTION_FIELD_MAPPINGS } from '../protocol/constants/sectionData';

/**
 * Maps changed clinical intake fields to affected protocol sections
 * @param {Array} changes - Array of change objects with field paths
 * @returns {Object} - Object with section titles as keys and arrays of affected fields as values
 */
export const getAffectedSections = (changes) => {
  if (!changes || changes.length === 0) {
    return {};
  }

  const affectedSections = {};

  // Iterate through all section mappings
  Object.entries(SECTION_FIELD_MAPPINGS).forEach(([sectionTitle, fieldMapping]) => {
    // SECTION_FIELD_MAPPINGS structure: { sectionTitle: { key: [fieldPaths] } }
    // We need to flatten all field paths from all keys in the mapping
    const allFieldPaths = [];
    
    if (typeof fieldMapping === 'object' && fieldMapping !== null) {
      Object.values(fieldMapping).forEach(paths => {
        if (Array.isArray(paths)) {
          allFieldPaths.push(...paths);
        } else if (typeof paths === 'string') {
          allFieldPaths.push(paths);
        }
      });
    } else if (Array.isArray(fieldMapping)) {
      allFieldPaths.push(...fieldMapping);
    }

    if (allFieldPaths.length === 0) {
      return;
    }

    // Check if any changed field matches this section's field paths
    const matchingChanges = changes.filter(change => {
      const changeField = change.field;
      
      // Check if the changed field matches any field path in this section
      return allFieldPaths.some(fieldPath => {
        // Handle exact matches
        if (changeField === fieldPath) {
          return true;
        }
        
        // Handle nested field matches (e.g., "study_treatments.treatment_table" matches "study_treatments.treatment_table.regimen1")
        if (changeField.startsWith(fieldPath + '.')) {
          return true;
        }
        
        // Handle parent field matches (e.g., "study_treatments" matches "study_treatments.treatment_table")
        if (fieldPath.startsWith(changeField + '.')) {
          return true;
        }
        
        return false;
      });
    });

    if (matchingChanges.length > 0) {
      affectedSections[sectionTitle] = {
        changes: matchingChanges,
        fieldPaths: allFieldPaths.filter(fp => 
          matchingChanges.some(change => 
            change.field === fp || 
            change.field.startsWith(fp + '.') || 
            fp.startsWith(change.field + '.')
          )
        )
      };
    }
  });

  return affectedSections;
};

/**
 * Gets all section titles that could be affected by any field change
 * @param {Array} changes - Array of change objects
 * @returns {Array} - Array of section titles
 */
export const getAffectedSectionTitles = (changes) => {
  const affectedSections = getAffectedSections(changes);
  return Object.keys(affectedSections);
};

/**
 * Determines if a specific section is affected by changes
 * @param {string} sectionTitle - The section title to check
 * @param {Array} changes - Array of change objects
 * @returns {boolean} - True if section is affected
 */
export const isSectionAffected = (sectionTitle, changes) => {
  const affectedSections = getAffectedSections(changes);
  return sectionTitle in affectedSections;
};

